import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Switch,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { FONT_SIZES } from '../../Theme';
import {
  getConsent,
  saveConsent,
} from '../../utils/cookies/cookieConsent';
import useLockBodyScroll from '../../hooks/shared/useLockBodyScroll';

const CATEGORY_COPY = [
  {
    key: 'necessary',
    label: 'Necessary',
    description:
      'Required for sign-in, security checks, and payments you request. Always on.',
    locked: true,
  },
  {
    key: 'functional',
    label: 'Functional',
    description:
      'Enables Google Maps on the Creator Map and address autocomplete in your profile.',
    locked: false,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description:
      'Helps us understand how the site is used. Not used today — reserved for future tools.',
    locked: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description:
      'Used for advertising or remarketing cookies. Not used today — reserved for future tools.',
    locked: false,
  },
];

function CookieSettingsDialog({ open, onClose, zIndex }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [draft, setDraft] = useState({
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (!open) return;
    const current = getConsent();
    setDraft({
      functional: Boolean(current?.functional),
      analytics: Boolean(current?.analytics),
      marketing: Boolean(current?.marketing),
    });
  }, [open]);

  useLockBodyScroll(open);

  const handleToggle = (key) => (event) => {
    setDraft((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handleSave = () => {
    saveConsent(draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableScrollLock
      aria-labelledby="cookie-settings-title"
      slots={{ transition: Slide }}
      slotProps={{
        transition: {
          direction: 'up',
          ...(reduceMotion ? { timeout: 0 } : null),
        },
      }}
      sx={{
        ...(zIndex != null ? { zIndex } : null),
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center' },
        },
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          m: { xs: 0, sm: 4 },
          width: { xs: '100%', sm: 'calc(100% - 64px)' },
          maxWidth: { xs: '100%', sm: 600 },
          maxHeight: { xs: '92dvh', sm: 'calc(100% - 64px)' },
          borderRadius: { xs: '20px 20px 0 0', sm: '16px' },
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="cookie-settings-title"
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
          fontSize: FONT_SIZES.title,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        Cookie settings
      </DialogTitle>
      <DialogContent
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2.5, lineHeight: 1.5, fontSize: FONT_SIZES.bodySm }}
        >
          Choose which optional cookies and similar technologies Millet may use.
          Necessary cookies stay on so the product works.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 2 } }}>
          {CATEGORY_COPY.map((category) => (
            <Box key={category.key}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2" component="p" sx={{ m: 0 }}>
                  {category.label}
                </Typography>
                <Switch
                  checked={category.locked || Boolean(draft[category.key])}
                  onChange={category.locked ? undefined : handleToggle(category.key)}
                  disabled={category.locked}
                  inputProps={{ 'aria-label': category.label }}
                  sx={{ flexShrink: 0, mr: -0.5 }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                  lineHeight: 1.45,
                  fontSize: FONT_SIZES.bodySm,
                }}
              >
                {category.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          pt: 1.5,
          pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { sm: 'flex-end' },
          gap: 1,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{
            minHeight: 44,
            width: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disableElevation
          sx={{
            minHeight: 44,
            width: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Save preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CookieSettingsDialog;
